import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

// Landing page
import PremiumLanding from './components/PremiumLanding';

// Auth Pages
import SupabaseLogin from './pages/Login';
import SupabaseRegister from './pages/Register';
import SupabaseApp from './pages/App';

// Landlord Dashboard Components
import DashboardLayout from './pages/DashboardLayout';
import DashboardHome from './pages/DashboardHome';
import PropertiesPage from './pages/PropertiesPage';
import PropertyDetailPage from './pages/PropertyDetailPage';
import TenantsPage from './pages/TenantsPage';
import TenantProfilePage from './pages/TenantProfilePage';
import PaymentsPage from './pages/PaymentsPage';
import PaymentAnalyticsPage from './pages/PaymentAnalyticsPage';
import MaintenancePage from './pages/MaintenancePage';
import DocumentsPage from './pages/DocumentsPage';
import SettingsPage from './pages/SettingsPage';

// Public pages
import SignPage from './pages/SignPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import ContactPage from './pages/ContactPage';

// Legal pages (GDPR / EU compliance)
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import TermsOfService from './pages/legal/TermsOfService';
import CookiePolicy from './pages/legal/CookiePolicy';
import Imprint from './pages/legal/Imprint';

// Cookie consent banner (mounted at root, hides itself inside /app/*)
import CookieBanner from './components/CookieBanner';

// Tenant Portal Components
import TenantLayout from './pages/tenant/TenantLayout';
import TenantDashboard from './pages/tenant/TenantDashboard';
import TenantPayments from './pages/tenant/TenantPayments';
import TenantMaintenance from './pages/tenant/TenantMaintenance';
import TenantDocuments from './pages/tenant/TenantDocuments';
import TenantSettings from './pages/tenant/TenantSettings';
import TenantConnect from './pages/tenant/TenantConnect';
import TenantChat from './pages/tenant/TenantChat';
import ChatPage from './pages/ChatPage';

// Plan gating
import PlanGate from './components/PlanGate';
import TenantFeatureGate from './components/TenantFeatureGate';

// Auth context for guards
import { useAppContext } from './context/useAppContext';

// ─── Loading screen ─────────────────────────────────────────────────────────

function AuthLoadingScreen() {
    return (
        <div style={{
            display: 'flex',
            height: '100vh',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#1e1f2e',
            color: '#9ca0b3',
            fontSize: '0.9rem',
            gap: '0.75rem',
        }}>
            <svg style={{ animation: 'spin 1s linear infinite' }} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
            </svg>
            Loading…
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
}

// ─── Role-based route guards ─────────────────────────────────────────────────
// These replace the layout components directly in the Route element prop.
// They check auth state + role before rendering the layout, ensuring tenants
// can never reach landlord routes and vice versa.

function LandlordRoute() {
    const { user, authLoading } = useAppContext();
    if (authLoading) return <AuthLoadingScreen />;
    if (!user.isAuthenticated) return <Navigate to="/login" replace />;
    if (user.role === 'tenant') return <Navigate to="/app/tenant/dashboard" replace />;
    return <DashboardLayout />;
}

function TenantRoute() {
    const { user, authLoading } = useAppContext();
    if (authLoading) return <AuthLoadingScreen />;
    if (!user.isAuthenticated) return <Navigate to="/login" replace />;
    if (user.role === 'landlord') return <Navigate to="/app/landlord/dashboard" replace />;
    return <TenantLayout />;
}

// ─── Router ───────────────────────────────────────────────────────────────────

export default function App() {
    return (
        <BrowserRouter>
            <Routes>
                {/* Public Landing */}
                <Route path="/" element={<PremiumLanding />} />

                {/* Auth routes */}
                <Route path="/login" element={<SupabaseLogin />} />
                <Route path="/register" element={<SupabaseRegister />} />

                {/* Role Redirector — reads profile.role and sends to correct dashboard */}
                <Route path="/app" element={<SupabaseApp />} />

                {/* Landlord Routes — LandlordRoute guards against tenant access */}
                <Route path="/app/landlord" element={<LandlordRoute />}>
                    <Route path="dashboard" element={<DashboardHome />} />
                    <Route path="properties" element={<PropertiesPage />} />
                    <Route path="properties/:id" element={<PropertyDetailPage />} />
                    <Route path="tenants" element={<TenantsPage />} />
                    <Route path="tenants/:id" element={<TenantProfilePage />} />
                    <Route path="payments" element={<PaymentsPage />} />
                    <Route path="payments/analytics" element={<PaymentAnalyticsPage />} />
                    <Route path="maintenance" element={<PlanGate feature="maintenance"><MaintenancePage /></PlanGate>} />
                    <Route path="documents" element={<DocumentsPage />} />
                    <Route path="chat" element={<PlanGate feature="chat"><ChatPage /></PlanGate>} />
                    <Route path="settings" element={<SettingsPage />} />
                </Route>

                {/* Tenant Routes — TenantRoute guards against landlord access */}
                <Route path="/app/tenant" element={<TenantRoute />}>
                    <Route path="dashboard" element={<TenantDashboard />} />
                    <Route path="connect" element={<TenantConnect />} />
                    <Route path="payments" element={<TenantPayments />} />
                    <Route path="maintenance" element={<TenantFeatureGate feature="maintenance" featureName="Maintenance requests"><TenantMaintenance /></TenantFeatureGate>} />
                    <Route path="documents" element={<TenantDocuments />} />
                    <Route path="chat" element={<TenantFeatureGate feature="chat" featureName="Chat"><TenantChat /></TenantFeatureGate>} />
                    <Route path="settings" element={<TenantSettings />} />
                </Route>

                {/* Public signing page — no auth required */}
                <Route path="/sign/:token" element={<SignPage />} />

                {/* Payment result pages */}
                <Route path="/payment/success" element={<PaymentSuccessPage />} />

                {/* Contact — public */}
                <Route path="/contact" element={<ContactPage />} />

                {/* Legal — public */}
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/cookies" element={<CookiePolicy />} />
                <Route path="/imprint" element={<Imprint />} />

                {/* Catch-all */}
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>

            <CookieBanner />
        </BrowserRouter>
    );
}
